import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, Loader2, ArrowRight, SearchX } from "lucide-react";
import { http, formatApiError } from "@/lib/api";
import { SiteFooter } from "@/components/Brand";
import { SiteHeader } from "@/components/SiteHeader";
import { CondominioResult } from "@/components/CondominioResult";

const Hero = ({ q, setQ, onSubmit, loading }) => (
  <section className="relative overflow-hidden border-b border-slate-200 bg-white">
    <div className="grid-bg pointer-events-none absolute inset-0" />
    <div className="relative mx-auto grid max-w-6xl gap-12 px-6 pb-20 pt-16 lg:grid-cols-[1.15fr_0.85fr] lg:pt-24">
      <div>
        <p className="fade-up text-xs font-semibold uppercase tracking-[0.2em] text-blue-600">Regolamento UE 2016/679 · GDPR</p>
        <h1 className="fade-up mt-4 font-heading text-4xl font-bold leading-[1.05] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl" style={{ animationDelay: "60ms" }}>
          Informative<br />per il tuo condominio.
        </h1>
        <p className="fade-up mt-6 max-w-lg text-base leading-relaxed text-slate-600" style={{ animationDelay: "120ms" }}>
          Per essere informato sulle caratteristiche del trattamento dei dati personali e sui diritti che la legge ti riconosce, consulta i documenti pubblicati per il tuo condominio.
        </p>
        <form onSubmit={onSubmit} className="fade-up mt-10 max-w-xl" style={{ animationDelay: "180ms" }} data-testid="search-form">
          <label htmlFor="cf" className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Codice fiscale del condominio</label>
          <div className="mt-2 flex flex-col gap-2 rounded-xl border border-slate-300 bg-white p-1.5 shadow-sm transition-shadow focus-within:border-slate-900 focus-within:shadow-[0_0_0_4px_rgba(37,99,235,0.12)] sm:flex-row">
            <input
              id="cf"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Es. 92033080265"
              data-testid="search-cf-input"
              autoComplete="off"
              className="h-12 flex-1 bg-transparent px-4 font-mono text-lg tracking-wide text-slate-900 outline-none placeholder:text-slate-300"
            />
            <button
              type="submit"
              disabled={loading || q.trim().length < 5}
              data-testid="search-submit-button"
              className="inline-flex h-12 items-center justify-center gap-2 rounded-lg bg-slate-900 px-6 font-medium text-white transition-colors hover:bg-blue-600 disabled:cursor-not-allowed disabled:bg-slate-300"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              Cerca documenti
            </button>
          </div>
        </form>
      </div>
      <div className="fade-up relative hidden lg:block" style={{ animationDelay: "240ms" }}>
        <img src="/hero.jpg" alt="Edificio residenziale" className="h-full max-h-[440px] w-full rounded-2xl object-cover grayscale-[30%]" />
        <div className="absolute -bottom-6 -left-6 rounded-xl border border-slate-200 bg-white/90 p-4 shadow-xl backdrop-blur-md">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-emerald-700">Accesso pubblico</p>
          <p className="mt-1 text-sm text-slate-700">Nessuna registrazione richiesta</p>
        </div>
      </div>
    </div>
  </section>
);

const Empty = ({ q }) => (
  <div data-testid="search-no-results" className="fade-up rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
    <SearchX className="mx-auto h-8 w-8 text-slate-400" />
    <p className="mt-3 font-heading text-lg font-semibold text-slate-900">Nessun condominio trovato</p>
    <p className="mt-1 text-sm text-slate-500">Nessun risultato per <span className="font-mono">{q}</span>. Verifica il codice inserito o contatta l'assistenza.</p>
  </div>
);

export default function PublicSearch() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const active = params.get("q") || "";

  useEffect(() => {
    if (!active) {
      setResults(null);
      return;
    }
    setLoading(true);
    setError("");
    http.get("/public/search", { params: { q: active } })
      .then(({ data }) => setResults(data))
      .catch((e) => { setError(formatApiError(e)); setResults(null); })
      .finally(() => setLoading(false));
  }, [active]);

  const onSubmit = (e) => {
    e.preventDefault();
    setParams({ q: q.trim() });
  };

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <SiteHeader />
      <Hero q={q} setQ={setQ} onSubmit={onSubmit} loading={loading} />
      <main className="mx-auto w-full max-w-6xl flex-1 px-6 py-14">
        {error && <p data-testid="search-error" className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">{error}</p>}
        {results && (
          <div className="space-y-6" data-testid="search-results">
            <div className="flex items-baseline justify-between">
              <h2 className="font-heading text-lg font-semibold text-slate-900">Risultati per <span className="font-mono text-blue-600">{active}</span></h2>
              <span className="text-sm text-slate-500">{results.length} condomin{results.length === 1 ? "io" : "i"}</span>
            </div>
            {results.length === 0 ? <Empty q={active} /> : results.map((c) => <CondominioResult key={c.codice_fiscale} c={c} />)}
          </div>
        )}
        {!results && !error && (
          <div className="grid gap-8 sm:grid-cols-3">
            {[["01", "Inserisci il codice", "Inserisci il codice fiscale del tuo condominio."], ["02", "Consulta i documenti", "Informativa privacy, contitolarità, videosorveglianza, antincendio e DUVRI."], ["03", "Scarica il PDF", "Ogni documento è sempre aggiornato con i dati del tuo condominio."]].map(([n, t, d]) => (
              <div key={n} className="border-t-2 border-slate-900 pt-4">
                <p className="font-mono text-xs text-slate-400">{n}</p>
                <p className="mt-2 flex items-center gap-2 font-heading font-semibold text-slate-900">{t} <ArrowRight className="h-3.5 w-3.5 text-blue-600" /></p>
                <p className="mt-1 text-sm leading-relaxed text-slate-500">{d}</p>
              </div>
            ))}
          </div>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
